import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, SlidersHorizontal, X, ChevronDown, Star,
  MessageCircle, Sparkles, ShoppingBag, Grid3X3, List
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import {
  getProducts,
  COLLECTIONS_LIST,
  PRODUCT_TYPES,
  SIZES_LIST,
  COLORS_LIST,
  OCCASIONS_LIST,
} from '../data/products';

const WHATSAPP = '918889616972';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    collection: true, type: true, size: true, color: true, occasion: true,
  });

  const [filters, setFilters] = useState({
    collections: searchParams.getAll('collection'),
    types: searchParams.getAll('type'),
    sizes: searchParams.getAll('size'),
    colors: searchParams.getAll('color'),
    occasions: searchParams.getAll('occasion'),
  });

  useEffect(() => {
    setProducts(getProducts());
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    filters.collections.forEach(v => params.append('collection', v));
    filters.types.forEach(v => params.append('type', v));
    filters.sizes.forEach(v => params.append('size', v));
    filters.colors.forEach(v => params.append('color', v));
    filters.occasions.forEach(v => params.append('occasion', v));
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const toggleFilter = (key, value) => {
    setFilters(prev => {
      const arr = prev[key];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const clearAllFilters = () => {
    setFilters({ collections: [], types: [], sizes: [], colors: [], occasions: [] });
    setSearch('');
  };

  const activeFilterCount =
    filters.collections.length + filters.types.length +
    filters.sizes.length + filters.colors.length + filters.occasions.length;

  const filtered = useMemo(() => {
    let result = [...products];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.includes(q))
      );
    }
    if (filters.collections.length) result = result.filter(p => filters.collections.includes(p.collection));
    if (filters.types.length) result = result.filter(p => filters.types.includes(p.type));
    if (filters.sizes.length) result = result.filter(p => (p.sizes || []).some(s => filters.sizes.includes(s)));
    if (filters.colors.length) result = result.filter(p => (p.colors || []).some(c => filters.colors.includes(c.name)));
    if (filters.occasions.length) result = result.filter(p => (p.occasion || []).some(o => filters.occasions.includes(o)));
    if (sortBy === 'price-asc') result.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') result.sort((a, b) => b.price - a.price);
    if (sortBy === 'newest') result.sort((a, b) => b.createdAt - a.createdAt);
    return result;
  }, [products, search, filters, sortBy]);

  const toggleSection = (key) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const handleWhatsAppOrder = (e, product) => {
    e.preventDefault();
    const msg = `Hi AuraPrintiqo! 👋\n\nI want to order:\n*${product.name}*\nPrice: ₹${product.price}\n\nPlease guide me on size, color & customisation options!`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  const FilterSection = ({ title, sectionKey, children }) => (
    <div className="border-b border-gray-100 pb-4 mb-4">
      <button onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between py-1 text-sm font-semibold text-gray-800 hover:text-[hsl(350,70%,55%)] transition-colors">
        {title}
        <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections[sectionKey] ? 'rotate-180' : ''}`} />
      </button>
      {expandedSections[sectionKey] && <div className="mt-3">{children}</div>}
    </div>
  );

  const FilterChip = ({ label, active, onClick }) => (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
        active ? 'bg-[hsl(350,70%,65%)] border-[hsl(350,70%,65%)] text-white'
               : 'bg-white border-gray-200 text-gray-600 hover:border-[hsl(350,70%,65%)] hover:text-[hsl(350,70%,55%)]'}`}>
      {label}
    </button>
  );

  const Sidebar = () => (
    <div className="space-y-0">
      <FilterSection title="Collection" sectionKey="collection">
        <div className="flex flex-wrap gap-2">
          {COLLECTIONS_LIST.map(c => (
            <FilterChip key={c.id} label={`${c.emoji} ${c.label}`}
              active={filters.collections.includes(c.id)}
              onClick={() => toggleFilter('collections', c.id)} />
          ))}
        </div>
      </FilterSection>
      <FilterSection title="Product Type" sectionKey="type">
        <div className="flex flex-wrap gap-2">
          {PRODUCT_TYPES.map(t => (
            <FilterChip key={t.id} label={t.label}
              active={filters.types.includes(t.id)}
              onClick={() => toggleFilter('types', t.id)} />
          ))}
        </div>
      </FilterSection>
      <FilterSection title="Size" sectionKey="size">
        <div className="flex flex-wrap gap-2">
          {SIZES_LIST.map(s => (
            <FilterChip key={s} label={s} active={filters.sizes.includes(s)}
              onClick={() => toggleFilter('sizes', s)} />
          ))}
        </div>
      </FilterSection>
      <FilterSection title="Color" sectionKey="color">
        <div className="flex flex-wrap gap-2">
          {COLORS_LIST.map(c => (
            <button key={c.name} onClick={() => toggleFilter('colors', c.name)} title={c.name}
              className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 ${
                filters.colors.includes(c.name) ? 'border-[hsl(350,70%,55%)] scale-110 shadow-md' : 'border-gray-200'}`}
              style={{ backgroundColor: c.hex }} />
          ))}
        </div>
        {filters.colors.length > 0 && <p className="text-xs text-gray-500 mt-2">{filters.colors.join(', ')}</p>}
      </FilterSection>
      <FilterSection title="Occasion" sectionKey="occasion">
        <div className="flex flex-wrap gap-2">
          {OCCASIONS_LIST.map(o => (
            <FilterChip key={o.id} label={o.label}
              active={filters.occasions.includes(o.id)}
              onClick={() => toggleFilter('occasions', o.id)} />
          ))}
        </div>
      </FilterSection>
    </div>
  );

  const ProductCard = ({ product }) => {
    const [imgIdx, setImgIdx] = useState(0);
    const discount = product.originalPrice
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : null;

    return (
      <div className="bg-white rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden border border-gray-50">
        <div className="relative overflow-hidden bg-gray-50" style={{ aspectRatio: '1/1' }}
          onMouseEnter={() => product.images[1] && setImgIdx(1)}
          onMouseLeave={() => setImgIdx(0)}>
          <img src={product.images[imgIdx]} alt={product.name} loading="lazy"
            className="w-full h-full object-cover transition-all duration-500 hover:scale-105" />
          <div className="absolute top-3 left-3 flex flex-col gap-1">
            {product.badge && (
              <span className="px-2 py-1 text-xs font-bold rounded-lg text-white shadow"
                style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}>
                {product.badge}
              </span>
            )}
            {discount && (
              <span className="px-2 py-1 text-xs font-bold rounded-lg bg-green-500 text-white shadow">-{discount}%</span>
            )}
          </div>
          {product.customisable && (
            <div className="absolute top-3 right-3">
              <span className="flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-medium text-[hsl(350,70%,55%)] shadow">
                <Sparkles className="w-3 h-3" /> Custom
              </span>
            </div>
          )}
          <div className="absolute bottom-3 left-3 right-3 opacity-0 hover:opacity-100 transition-all duration-200">
            <button onClick={(e) => handleWhatsAppOrder(e, product)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg"
              style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)' }}>
              <MessageCircle className="w-4 h-4" /> Order via WhatsApp
            </button>
          </div>
        </div>
        <div className="p-4">
          <p className="text-xs text-[hsl(350,70%,55%)] font-medium mb-1">
            {COLLECTIONS_LIST.find(c => c.id === product.collection)?.emoji}{' '}
            {COLLECTIONS_LIST.find(c => c.id === product.collection)?.label}
          </p>
          <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-1">{product.name}</h3>
          <div className="flex items-center gap-1 mb-3">
            {(product.colors || []).slice(0, 5).map(c => (
              <div key={c.name} title={c.name} className="w-4 h-4 rounded-full border border-gray-200"
                style={{ backgroundColor: c.hex }} />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="font-bold text-gray-900">₹{product.price}</span>
              {product.originalPrice && (
                <span className="text-xs text-gray-400 line-through ml-2">₹{product.originalPrice}</span>
              )}
            </div>
            <div className="flex gap-1">
              {(product.sizes || []).slice(0, 3).map(s => (
                <span key={s} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <section className="py-10 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--peach))]/30 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-3">
              <ShoppingBag className="w-4 h-4" /> Our Shop
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Browse & Order</h1>
            <p className="text-gray-600">Choose a ready-made design or customise any product with your own photo</p>
          </div>
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search designs, collections, occasions..."
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-soft text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30 focus:border-[hsl(350,70%,65%)]" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-32 bg-white rounded-2xl shadow-soft p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 text-sm">Filters</h3>
                {activeFilterCount > 0 && (
                  <button onClick={clearAllFilters} className="text-xs text-[hsl(350,70%,55%)] font-medium hover:underline">
                    Clear all ({activeFilterCount})
                  </button>
                )}
              </div>
              <Sidebar />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-3">
                <button onClick={() => setFiltersOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:border-[hsl(350,70%,65%)] transition-colors">
                  <SlidersHorizontal className="w-4 h-4" /> Filters
                  {activeFilterCount > 0 && (
                    <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center"
                      style={{ background: 'hsl(350,70%,65%)' }}>{activeFilterCount}</span>
                  )}
                </button>
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">{filtered.length}</span> products
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30 bg-white">
                  <option value="newest">Newest First</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                </select>
                <div className="hidden sm:flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
                  <button onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white shadow text-[hsl(350,70%,55%)]' : 'text-gray-400'}`}>
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white shadow text-[hsl(350,70%,55%)]' : 'text-gray-400'}`}>
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {filters.collections.map(v => (
                  <span key={v} className="flex items-center gap-1 px-3 py-1 bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)] rounded-full text-xs font-medium">
                    {COLLECTIONS_LIST.find(c => c.id === v)?.label}
                    <button onClick={() => toggleFilter('collections', v)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {filters.types.map(v => (
                  <span key={v} className="flex items-center gap-1 px-3 py-1 bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)] rounded-full text-xs font-medium">
                    {v} <button onClick={() => toggleFilter('types', v)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {filters.sizes.map(v => (
                  <span key={v} className="flex items-center gap-1 px-3 py-1 bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)] rounded-full text-xs font-medium">
                    Size: {v} <button onClick={() => toggleFilter('sizes', v)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {filters.colors.map(v => (
                  <span key={v} className="flex items-center gap-1 px-3 py-1 bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)] rounded-full text-xs font-medium">
                    {v} <button onClick={() => toggleFilter('colors', v)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {filters.occasions.map(v => (
                  <span key={v} className="flex items-center gap-1 px-3 py-1 bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)] rounded-full text-xs font-medium">
                    {OCCASIONS_LIST.find(o => o.id === v)?.label}
                    <button onClick={() => toggleFilter('occasions', v)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No products found</h3>
                <p className="text-gray-500 text-sm mb-4">Try adjusting your filters or search</p>
                <button onClick={clearAllFilters}
                  className="px-6 py-2 rounded-xl text-white text-sm font-medium"
                  style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}>
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {filtered.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Filters</h3>
              <div className="flex items-center gap-3">
                {activeFilterCount > 0 && (
                  <button onClick={clearAllFilters} className="text-xs text-[hsl(350,70%,55%)] font-medium">Clear all</button>
                )}
                <button onClick={() => setFiltersOpen(false)}><X className="w-5 h-5 text-gray-500" /></button>
              </div>
            </div>
            <div className="p-5"><Sidebar /></div>
            <div className="sticky bottom-0 p-4 bg-white border-t border-gray-100">
              <button onClick={() => setFiltersOpen(false)}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm"
                style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}>
                Show {filtered.length} Products
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ShopPage;
